"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { Issue } from "@/types/issues";
import { format } from "date-fns";
import AssigneeDropdown from "./assignee-dropdown";
import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import { IssueContextMenu } from "./issue-context-menu";
import { Badge } from "@/components/ui/badge";
import { DueDateBadge } from "@/components/common/badges/due-date-badge";
import { EstimateBadge } from "@/components/common/badges/estimate-badge";
import { LabelBadge } from "@/components/common/badges/label-badge";
import { Status } from "@/types/status";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useIssuesStore } from "@/store/issues-store";
import { useSearchParams } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { cn } from "@/lib/utils";
import { useFocusStore } from "@/store/zustand/focusStore";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { PriorityBadge } from "@/components/common/badges/priority-badge";
import { shouldIgnoreMouseHover } from "@/hooks/useIssueKeyboardShortcuts";
import { LabelInterface } from "@/types/labels";
import { StatusBadge } from "../badges/status-badge";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";

type IssueGridProps = {
  issue: Issue;
  users?: any[];
  labels?: any[];
  statuses: Status[];
  onAssignUser?: (issueId: string, userId: string | null) => void;
  onClick?: () => void;
  provided?: any;
  snapshot?: any;
  icon?: () => JSX.Element;
  issueRefs?: { [key: string]: HTMLDivElement };
  visibleIssueIds?: string[]; // For selection range calculations
};

// Configure hover delay (in milliseconds)
const HOVER_DELAY = 100;

export const IssueGrid = React.forwardRef<HTMLDivElement, IssueGridProps>(
  (
    {
      issue,
      users = [],
      labels = [],
      statuses = [],
      onAssignUser,
      onClick,
      provided,
      snapshot,
      icon,
      issueRefs,
      visibleIssueIds,
    },
    ref
  ) => {
    // The number of labels to show before starting to stack
    const LABEL_STACK_THRESHOLD = 5;
    // Once stacking starts, the number of labels to show before the stack
    const PRE_STACK_LABEL_COUNT = 4;
    const [searchParams] = useSearchParams();
    const currentProjectId = searchParams.get("project_id");

    const { setLastFocusedIssue, focusedIssueId } = useFocusStore();

    const isFocused = focusedIssueId === issue.id;

    // Issue selection hook
    const { selectedIssueIds, toggleIssueSelection } = useIssueSelectionStore();
    const isSelected = selectedIssueIds.includes(issue.id);

    // Refs for hover tracking
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isHoveredRef = useRef(false);

    function splitLabels(labels: LabelInterface[]) {
      const [firstFive, rest] = labels.slice(0, 5);
      return { firstFive, rest };
    }

    // Clear hover timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    const handleMouseEnter = useCallback(() => {
      // Skip hover focus if keyboard operation is active
      if (shouldIgnoreMouseHover()) return;

      isHoveredRef.current = true;
      // Start the delay timer only on enter
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        if (isHoveredRef.current && !shouldIgnoreMouseHover()) {
          setLastFocusedIssue(issue.id);
          // Clear the timeout ref to indicate timer has completed
          hoverTimeoutRef.current = null;
        }
      }, HOVER_DELAY);
    }, [issue.id]);

    const handleMouseMove = useCallback(() => {
      // Skip hover focus if keyboard operation is active
      if (shouldIgnoreMouseHover()) return;

      // If timer has expired (null) and we're not already focused on this issue,
      // set focus immediately
      if (!hoverTimeoutRef.current && focusedIssueId !== issue.id) {
        setLastFocusedIssue(issue.id);
      }
    }, [focusedIssueId, issue.id]);

    const handleMouseLeave = useCallback(() => {
      isHoveredRef.current = false;
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }, []);

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        // Set last focused issue on any direct interaction
        setLastFocusedIssue(issue.id);
        // Handle selection logic
        onClick && onClick();
      },
      [issue.id, onClick]
    );

    // Only apply special drag styling if we're actually dragging
    const style = snapshot?.isDragging
      ? {
          ...provided?.draggableProps?.style,
          cursor: "grabbing",
          boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
          opacity: 0.9,
        }
      : {
          ...provided?.draggableProps?.style,
          cursor: "default",
        };

    return (
      <ContextMenu
        onOpenChange={(open) => {
          if (open) {
            useProjectModalStore
              .getState()
              .addProjectModal("issue-context-menu");
          } else {
            useProjectModalStore
              .getState()
              .removeProjectModal("issue-context-menu");
          }
        }}
      >
        <ContextMenuTrigger
          asChild
          onContextMenu={() => setLastFocusedIssue(issue.id)}
        >
          <div
            ref={(el) => {
              // Handle the forwarded ref
              if (typeof ref === "function") {
                ref(el);
              } else if (ref) {
                ref.current = el;
              }
              // Store in our issue refs map
              if (el && issueRefs) {
                issueRefs[issue.id] = el;
              }
              // Handle the drag and drop ref
              if (provided?.innerRef) {
                provided.innerRef(el);
              }
            }}
            {...(provided?.draggableProps || {})}
            {...(provided?.dragHandleProps || {})}
            className={cn(
              "w-full bg-secondary-n-950 rounded-lg shadow-xs cursor-default flex flex-col gap-2 p-2",
              !isSelected && "transition-colors hover:bg-secondary-n-950",
              isFocused && "ring-1 ring-primary-0 ring-inset",
              isSelected && ["bg-primary-n-600/20"],
              snapshot?.isDragging && "shadow-lg"
            )}
            style={style}
            data-issue-id={issue.id}
            data-focused={isFocused ? "true" : "false"}
            data-selected={isSelected ? "true" : "false"}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            {/* --- TOP ROW: Status Icon, Identifier, Assignee --- */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className="body text-xs font-normal text-secondary-n-400 truncate max-w-[100px]">
                  {issue.identifier}
                </span>
              </div>
              <AssigneeDropdown
                issueId={issue.id}
                users={users}
                value={issue.assignee?.id ?? null}
                onChange={(userId) =>
                  onAssignUser && onAssignUser(issue.id, userId)
                }
                size={20}
              />
            </div>

            {/* --- TITLE --- */}
            <div className="flex items-center gap-1">
              <StatusBadge
                status={issue.status}
                issueId={issue.id}
                statuses={statuses}
              />
              <span className="body text-xs font-medium">{issue.title}</span>
            </div>

            {/* --- BOTTOM ROW: Labels, Priority, Estimate, Due Date --- */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Priority badge */}
              <PriorityBadge
                priority={issue.priority}
                issueId={issue.id}
                featurePrdId={issue.getRawFeaturePRD()?.id}
                hasOutline={true}
              />
              {/* estimate badge */}
              {issue.estimate && issue.estimate > 0 && (
                <EstimateBadge
                  estimate={issue.estimate}
                  issueId={issue.id}
                  featurePrdId={issue.getRawFeaturePRD()?.id}
                />
              )}
              {/* Labels */}
              {issue.labels && issue.labels.length > 0 ? (
                issue.labels.length <= LABEL_STACK_THRESHOLD ? (
                  issue.labels.map((label) => (
                    <LabelBadge
                      key={label}
                      labelId={label}
                      issue={issue}
                      projectLabels={labels}
                    />
                  ))
                ) : (
                  <>
                    {issue.labels
                      .slice(0, PRE_STACK_LABEL_COUNT)
                      .map((label) => (
                        <LabelBadge
                          key={label}
                          labelId={label}
                          issue={issue}
                          projectLabels={labels}
                        />
                      ))}
                    <LabelBadge
                      key={issue.labels[0]}
                      labelId={issue.labels[0]}
                      issue={issue}
                      projectLabels={labels}
                      labelsToStack={(
                        issue.labels.map((label) =>
                          labels?.find((l) => l.id === label)
                        ) ?? []
                      ).slice(PRE_STACK_LABEL_COUNT)}
                    />
                  </>
                )
              ) : (
                <></>
              )}
              {/* Due Date badge - last badge */}
              {issue.dueDate && issue.dueDate !== "" && (
                <DueDateBadge
                  dueDate={issue.dueDate}
                  issueId={issue.id}
                  featurePrdId={issue.getRawFeaturePRD()?.id}
                />
              )}
            </div>
          </div>
        </ContextMenuTrigger>
        <IssueContextMenu
          issueIds={selectedIssueIds.length > 0 ? selectedIssueIds : [issue.id]}
          users={users}
          labels={labels}
          status={statuses}
        />
      </ContextMenu>
    );
  }
);

IssueGrid.displayName = "IssueGrid";
